import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { useNavigate, Link } from "react-router-dom";
import { API_BASE_URL, API_PREFIX, API_ENDPOINTS } from "../constants/config";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { logoCache, profileService } from "../services/apiService";


const API_URL = `${API_BASE_URL}${API_PREFIX}`;
const typingTexts = ["Put your mobile number...", "Type something..."];
const passwordTexts = ["Put password here...", "Don't forget it..."];

const TypingPlaceholder = ({ texts, isVisible }) => {
  const [index, setIndex] = useState(0);
  const [display, setDisplay] = useState("");

  useEffect(() => {
    if (!isVisible) return;

    const fullText = texts[index];
    let charIndex = 0;

    const typeInterval = setInterval(() => {
      setDisplay(fullText.substring(0, charIndex + 1));
      charIndex++;
      if (charIndex === fullText.length) {
        clearInterval(typeInterval);
        setTimeout(() => {
          setIndex((index + 1) % texts.length);
        }, 1500);
      }
    }, 100);

    return () => clearInterval(typeInterval);
  }, [index, isVisible, texts]);

  return (
    <span className="text-white text-sm absolute left-4 top-5.5 pointer-events-none opacity-50">
      {isVisible ? display : ""}
    </span>
  );
};

const Login = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [userName, setUserName] = useState("");
  const [password, setPassword] = useState("");
  const [clientCode, setClientCode] = useState("");
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const handleSubmit = async (event) => {
    event.preventDefault();

    localStorage.removeItem("token");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("clientLogoDataUrl");
    logoCache.clear();

    setError("");
    if (!clientCode.trim()) {
      setError("Client Code is required");
      return;
    }
    if (!userName.trim()) {
      setError("Mobile Number is required");
      return;
    }
    if (!password.trim()) {
      setError("Password is required");
      return;
    }

    try {
      const res = await axios.post(`${API_URL}${API_ENDPOINTS.LOGIN}`, {
        mobileNo: userName,
        password,
        clientCode,
      });

      const data = res.data;
      const token = data.accessToken || "";
      const refreshToken = data.refreshToken || "";

      // Decode JWT to extract clientName and roles
      let clientName = clientCode;
      let userRole = "";
      try {
        const payload = JSON.parse(atob(token.split(".")[1]));
        clientName = payload.clientName || clientCode;
        userRole = payload.roles?.[0] || "";
      } catch {}

      localStorage.setItem("token", token);
      localStorage.setItem("userName", userName);
      localStorage.setItem("refreshToken", refreshToken);
      localStorage.setItem("userRole", userRole);
      localStorage.setItem("companyName", clientName);
      localStorage.setItem("clientCode", clientCode);

      if (!token) {
        setError("Login failed: no token received");
        return;
      }

      // Fetch logo before navigating so Header shows it immediately — no flicker
      logoCache.clear();
      try {
        const blob = await profileService.getClientLogo();
        if (blob && blob.size > 0) {
          const reader = new FileReader();
          await new Promise((res) => {
            reader.onloadend = () => { logoCache.set(reader.result); res(); };
            reader.readAsDataURL(blob);
          });
        }
      } catch {}

      navigate("/home");
    } catch (err) {
      const data = err.response?.data;
      const message =
        data?.errorMessage ||
        data?.error ||
        "Server error. Please try again.";
      setError(message);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="images/background.jpg"
          alt="Medicine"
          className="w-full h-full object-cover brightness-100 contrast-100"
        />
      </div>

      <motion.div
        className="relative z-10 bg-black bg-opacity-50 backdrop-blur-sm rounded-xl p-10 shadow-xl border border-white border-opacity-10 w-[90%] max-w-md"
        whileHover={{ scale: 1.05 }}
        transition={{ duration: 0.3 }}
      >
        <motion.h2
          className="text-center text-2xl font-bold mb-6 
             bg-gradient-to-r from-pink-500 via-yellow-400 to-cyan-500 
             bg-clip-text text-transparent animate-gradient-slow bg-[length:400%_400%] 
             [text-shadow:_2px_2px_8px_rgba(255,255,255,0.4)]"
          initial={{ opacity: 0, scale: 0.8, rotateX: -30 }}
          animate={{ opacity: 1, scale: 1, rotateX: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          whileHover={{ scale: 1.05, rotateY: 5 }}
        >
          JaShBill
        </motion.h2>

        <form onSubmit={handleSubmit}>
          <div className="relative mb-6">
            <input
              type="text"
              value={userName}
              onChange={(e) => setUserName(e.target.value)}
              autoComplete="username"
              className="w-full px-4 py-2 bg-white/20 border border-white/30 rounded-md text-white focus:outline-none"
            />
            {userName === "" && (
              <TypingPlaceholder texts={typingTexts} isVisible={true} />
            )}
          </div>

          <div className="relative mb-6">
            <input
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              className="w-full px-4 py-2 pr-10 bg-white/20 border border-white/30 rounded-md text-white focus:outline-none"
            />

            {/* Placeholder (separate) */}
            {password === "" && (
              <TypingPlaceholder texts={passwordTexts} isVisible={true} />
            )}

            {/* Eye Icon */}
            <span
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer text-white"
            >
              {showPassword ? <FaEyeSlash /> : <FaEye />}
            </span>
          </div>

          <div className="relative mb-6">
            <input
              type="text"
              value={clientCode}
              onChange={(e) => setClientCode(e.target.value.toUpperCase())}
              autoComplete="off"
              className="w-full px-4 py-2 bg-white/20 border border-white/30 rounded-md text-white focus:outline-none uppercase"
            />
            {clientCode === "" && (
              <TypingPlaceholder
                texts={["Client Code...", "Enter your client code..."]}
                isVisible={true}
              />
            )}
          </div>

          <div className="flex items-center justify-between">
            <button
              type="submit"
              className="w-full bg-white text-black font-bold py-2 rounded-md hover:bg-opacity-80 transition"
            >
              Click Me
            </button>

            {error && (
              <motion.div
                className="absolute top-[65%] left-full ml-2 
               bg-red-500 text-white text-sm px-4 py-2 rounded-full shadow-lg border border-red-700 
               flex items-center z-20
               before:content-[''] before:absolute before:top-1/2 before:-left-2 before:-translate-y-1/2 
               before:border-y-[8px] before:border-r-[12px] before:border-l-0 
               before:border-y-transparent before:border-r-red-500"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.4 }}
              >
                ⚠️ {error}
              </motion.div>
            )}
          </div>
        </form>
        <div className="text-center mt-4">
          <span className="text-white text-sm">Don't have an account? </span>
          <Link
            to="/signup"
            className="text-blue-400 hover:text-blue-200 font-semibold transition"
          >
            Sign Up
          </Link>
        </div>
      </motion.div>
    </div>
  );
};

export default Login;
